import React from 'react';
import { ChildProps } from 'react-apollo';
import I18n from 'i18n-js';
import moment from 'moment';
import { LineChartData } from 'chartist';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';

import LineGraph from '../../graph/line_graph';
import { buildChartData, buildChartDescription, DATE_FORMAT } from '../../show/price_guide_tool_transaction_graph_helpers';
import { ButtonGroup, ButtonGroupButton } from '../../show/button_group';
import {
  CSP,
  SearchPriceGuideToolTransactionGraph,
} from '../../../../schemas/rql';
import {
  QUERY,
  SELLER_COUNTRY_CODES,
  DURATION_IN_MONTHS,
  DATE_RANGE_SELECTORS,
  PAID_TRANSACTION_STATUSES,
  LABEL_TILT_DEGREES,
  PriceGuideToolTransactionGraphEmptyState,
  createdAfterTwoYearsAgoDate,
} from '../../show/price_guide_tool_transaction_graph_container';
import { useIsMobileWindowWidth } from '@reverbdotcom/commons/src/is-mobile';

export const MOBILE_CHART_HEIGHT = '150px';
export const DEFAULT_CHART_HEIGHT = '285px';

interface IExternalProps {
  csp: CSP;
  conditionUuids?: string[];
  canonicalProductIds?: string[];
}

type IProps = ChildProps<IExternalProps, SearchPriceGuideToolTransactionGraph.Query>;

export function chartOptions(user, isMobile) {
  return {
    height: isMobile ? MOBILE_CHART_HEIGHT : DEFAULT_CHART_HEIGHT,
    showArea: true,
    axisY: {
      showGrid: true,
      showLabel: true,
      labelInterpolationFnc: (val) => parseAmount(val, user?.currency || 'USD', { precision: 0 }).display,
    },
  };
}

function canonicalProductIdsForGraph({ canonicalProductIds, csp }) {
  return canonicalProductIds ?? csp.variants.map(variant => variant.id);
}

export function PriceHistoryGraphContainer({
  csp,
  conditionUuids,
  data,
}: IProps) {
  const user = useUser();
  const isV2LayoutEnabled = useExpEnabled(experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2);

  const isMobile = useIsMobileWindowWidth();
  const [dateRange, setDateRange] = React.useState(DATE_RANGE_SELECTORS[0]);

  const { loading, priceRecordsSearch = {} } = data;
  const { averageMonthlyProductPrices: productPrices = [] } = priceRecordsSearch;

  const conditionSelected = conditionUuids.length === 1;
  const conditionSlug = conditionSlugForUuid(conditionUuids[0]);
  const conditionText = conditionSelected ?
    I18n.t(`discovery.conditions.${conditionSlug}`).toLowerCase() :
    I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.used');

  const totalOrders = productPrices.reduce((acc, field) => acc + field.docCount, 0);

  const shouldRenderEmptyState = !totalOrders;

  const currentMonth = moment().startOf('month');
  const monthsSubtractedFromCurrentMonth = DURATION_IN_MONTHS[dateRange] - 1;
  const createdAfterDate = currentMonth.subtract(monthsSubtractedFromCurrentMonth, 'months').format(DATE_FORMAT);

  const chartData = totalOrders ? buildChartData({ productPrices, createdAfterDate }) : {} as LineChartData;
  const labelTiltDegrees = LABEL_TILT_DEGREES[dateRange];

  React.useEffect(() => {
    trackEvent({
      eventName: MParticleEventName.ClickedDateRangeButtonOnCSPPriceGuideGraph,
      componentName: 'PriceHistoryGraphContainer',
      cspId: csp.id,
      cspSlug: csp.slug,
      dateRange,
      experiments: formatExperimentsForEventAttributes(user, [
        experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
      ]),
    });
  }, [dateRange]);

  function tooltipCallback(meta) {
    const titleText = I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.tooltip.productsSold', { count: meta.docCount });
    const priceValueText = I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.tooltip.amountAverage', {
      formattedAmount: meta.averageProductPrice.display,
      dateString: moment(meta.date).format('MMM YYYY'),
    });
    const conditionSection = I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.tooltip.condition', {
      conditionText,
    });

    return `
      <div class="price-guide-tool-transaction-graph__tooltip">
        <h3 class="price-guide-tool-transaction-graph__tooltip__title">
          ${titleText}
        </h3>
        <span class="price-guide-tool-transaction-graph__tooltip__price-value">
          ${priceValueText}
        </span>
        <span class="price-guide-tool-transaction-graph__tooltip__condition">
          ${conditionSection}
        </span>
      </div>
    `;
  }

  if (loading) return null;

  return (
    <div className="price-history-graph-container">
      <div className="price-history-graph-container__title">
        {I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.priceHistory')}
      </div>
      {shouldRenderEmptyState ? (
        <PriceGuideToolTransactionGraphEmptyState />
      ) : (
        <>
          {!isV2LayoutEnabled && (
            <div>
              {I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.tooltip.basedOn', { count: totalOrders })}
            </div>
          )}
          
          <LineGraph
            chartData={chartData}
            chartOptions={chartOptions(user, isMobile)}
            tooltipCallback={tooltipCallback}
            labelTiltDegrees={labelTiltDegrees}
            ariaLabel={I18n.t('discovery.priceGuideTool.show.form.results.transactionGraph.ariaLabel')}
            ariaDescription={buildChartDescription(chartData)}
          />
          <div className="fx-align-self-end">
            <ButtonGroup>
              {DATE_RANGE_SELECTORS.map((range, index) => (
                <ButtonGroupButton
                  key={range}
                  onClick={() => setDateRange(DATE_RANGE_SELECTORS[index])}
                  selected={dateRange === DATE_RANGE_SELECTORS[index]}
                >
                  {I18n.t(`discovery.priceGuideTool.show.form.results.transactionGraph.buttonGroup.${range}`)}
                </ButtonGroupButton>
              ))}
            </ButtonGroup>
          </div>
          <I18N
            className="size-80 color-secondary mb-0 mt-2 align-right"
            tag="p"
            html
            text="discovery.priceGuideTool.cspModule.graph.disclaimer"
            args={{ supOpen: '<sup>', supClose: '</sup>' }}
          />
        </>
      )}
    </div>
  );
}

const withQuery = withGraphql<IProps, SearchPriceGuideToolTransactionGraph.Query>(
  QUERY,
  {
    options: ({ csp, canonicalProductIds, conditionUuids }) => {
      const cpIds = canonicalProductIdsForGraph({ canonicalProductIds, csp });

      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: {
          canonicalProductIds: cpIds,
          conditionUuids: conditionUuids || [],
          sellerCountries: SELLER_COUNTRY_CODES,
          createdAfterDate: createdAfterTwoYearsAgoDate(),
          actionableStatuses: PAID_TRANSACTION_STATUSES,
        },
      };
    },
  },
);

export default withQuery(props => {
  return <PriceHistoryGraphContainer {...props} />;
});
